import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { OrganizationPicker } from '../common';
import {
  Shield, Users, Search, Check, X, Building2, Mail,
  AlertCircle, Loader2, ChevronDown, ChevronUp, History, Eye, Edit3, Trash2
} from 'lucide-react';

// Permission type definitions with icons, colors, and categories
const PERMISSION_TYPES = {
  // App Administration
  app_administration: { label: 'App Administration', icon: Shield, color: 'purple', description: 'Access to App Administration (manage user permissions)', category: 'App Administration' },
  // Organizations & Packages
  organizations: { label: 'Organizations', icon: Building2, color: 'blue', description: 'Manage organizations (create, edit, delete)', category: 'Organizations & Packages' },
  packages: { label: 'Packages', icon: Shield, color: 'indigo', description: 'Manage packages and package assignments', category: 'Organizations & Packages' },
  // Content & Marketing
  landing_pages: { label: 'Landing Pages', icon: Shield, color: 'green', description: 'Access landing page management', category: 'Content & Marketing' },
  early_access: { label: 'Early Access', icon: Mail, color: 'yellow', description: 'View and manage early access requests', category: 'Content & Marketing' },
  legal_documents: { label: 'Legal Documents', icon: Shield, color: 'emerald', description: 'Access to Legal Documents management', category: 'Content & Marketing' },
  // User Management
  users_invites: { label: 'Users & Invites', icon: Users, color: 'sky', description: 'Manage users and invitations across organizations', category: 'User Management' },
  teams_permissions: { label: 'Teams & Permissions', icon: Shield, color: 'pink', description: 'Manage teams and user permissions', category: 'User Management' },
  // Billing & Finance
  admin_billing: { label: 'Admin Billing', icon: Shield, color: 'rose', description: 'Access to Admin Billing dashboard', category: 'Billing & Finance' },
  billing_integrations: { label: 'Billing & Integrations', icon: Shield, color: 'violet', description: 'Access to Billing & Integrations settings', category: 'Billing & Finance' },
  billing_scheduler: { label: 'Billing Scheduler', icon: Shield, color: 'cyan', description: 'Manage billing scheduler (view status, trigger runs, configure)', category: 'Billing & Finance' },
  billing_refunds: { label: 'Billing Refunds', icon: Shield, color: 'red', description: 'Request and approve refunds across the platform', category: 'Billing & Finance' },
  billing_invoice_void: { label: 'Invoice Void', icon: Shield, color: 'red', description: 'Void invoices across the platform', category: 'Billing & Finance' },
  billing_audit: { label: 'Billing Audit', icon: Shield, color: 'slate', description: 'View platform-wide billing audit logs', category: 'Billing & Finance' },
  billing_direct_bill_approve: { label: 'Direct Bill Approve', icon: Shield, color: 'amber', description: 'Approve direct bill payment type for subscriptions', category: 'Billing & Finance' },
  // Parsing & Data
  parsing_feedback: { label: 'Parsing & Data', icon: AlertCircle, color: 'orange', description: 'Access to Parsing Dashboard, Review Queue, and settings', category: 'Parsing & Data' },
  // System
  logs: { label: 'Logs', icon: AlertCircle, color: 'gray', description: 'Access to system logs (API, parsing, FlightAware, Google Places)', category: 'System' }
};

const AdminPermissionsManagement = () => {
  const [activeTab, setActiveTab] = useState('permissions'); // 'permissions' or 'org_access'
  const [allUsers, setAllUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgAccessGrants, setOrgAccessGrants] = useState([]);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOrgAccessModal, setShowOrgAccessModal] = useState(false);
  const [selectedUserForOrg, setSelectedUserForOrg] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('view_only');
  const { user } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const [typesRes, allUsersRes, orgsRes, grantsRes] = await Promise.all([
        axios.get(`${apiBase}/admin/permissions/types`, { headers }),
        axios.get(`${apiBase}/admin/permissions/users/all`, { headers }),
        axios.get(`${apiBase}/admin/organizations`, { headers }),
        axios.get(`${apiBase}/admin/organization-access/grants`, { headers })
      ]);

      setPermissionTypes(typesRes.data);
      setAllUsers(allUsersRes.data);
      setOrganizations(orgsRes.data);
      setOrgAccessGrants(grantsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load admin permissions data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadData();
    }
  }, [user, loadData]);

  const loadAuditLog = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/permissions/audit-log?limit=50`, { headers });
      setAuditLog(res.data);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    }
  };

  const handleSetAllPermissions = async (userId, permissions) => {
    setSaving(prev => ({ ...prev, [userId]: true }));

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.put(
        `${apiBase}/admin/permissions/user/${userId}`,
        { permissions },
        { headers }
      );

      await loadData();
      setShowUserModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to update permissions:', err);
      setError('Failed to update permissions');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleGrantOrgAccess = async () => {
    if (!selectedUserForOrg || !selectedOrg) return;

    setSaving(prev => ({ ...prev, orgAccess: true }));

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.post(
        `${apiBase}/admin/organization-access/user/${selectedUserForOrg.id}/grant`,
        {
          organization_id: selectedOrg,
          access_level: selectedAccessLevel
        },
        { headers }
      );

      await loadData();
      setShowOrgAccessModal(false);
      setSelectedUserForOrg(null);
      setSelectedOrg('');
      setSelectedAccessLevel('view_only');
    } catch (err) {
      console.error('Failed to grant org access:', err);
      setError(err.response?.data?.detail || 'Failed to grant organization access');
    } finally {
      setSaving(prev => ({ ...prev, orgAccess: false }));
    }
  };

  const handleRevokeOrgAccess = async (userId, organizationId) => {
    const key = `revoke-${userId}-${organizationId}`;
    setSaving(prev => ({ ...prev, [key]: true }));

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.post(
        `${apiBase}/admin/organization-access/user/${userId}/revoke?organization_id=${organizationId}`,
        {},
        { headers }
      );

      await loadData();
    } catch (err) {
      console.error('Failed to revoke org access:', err);
      setError('Failed to revoke organization access');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const openUserModal = (user) => {
    setSelectedUser({
      ...user,
      admin_permissions: user.admin_permissions || []
    });
    setShowUserModal(true);
  };

  const openOrgAccessModal = (user) => {
    setSelectedUserForOrg(user);
    setSelectedOrg('');
    setSelectedAccessLevel('view_only');
    setShowOrgAccessModal(true);
  };

  // Filter users based on search term
  const filteredUsers = allUsers.filter(u => {
    const searchLower = searchTerm.toLowerCase();
    return (
      u.email?.toLowerCase().includes(searchLower) ||
      u.first_name?.toLowerCase().includes(searchLower) ||
      u.last_name?.toLowerCase().includes(searchLower) ||
      u.organization_name?.toLowerCase().includes(searchLower)
    );
  });

  // Get users with permissions for the summary view
  const usersWithAnyPermissions = allUsers.filter(u => u.admin_permissions && u.admin_permissions.length > 0);

  // Get organizations user can be granted access to (exclude their own)
  const getAvailableOrgsForUser = (userId) => {
    const userObj = allUsers.find(u => u.id === userId);
    if (!userObj) return organizations;

    // Get orgs already granted
    const grantedOrgIds = orgAccessGrants
      .filter(g => g.user_id === userId)
      .map(g => g.organization_id);

    return organizations.filter(org =>
      org.id !== userObj.organization_id && !grantedOrgIds.includes(org.id)
    );
  };

  const isSuperadmin = user?.role === 'superadmin';
  const hasAppAdminPermission = user?.admin_permissions?.includes('app_administration');
  const canAccessAppAdmin = isSuperadmin || hasAppAdminPermission;

  if (!canAccessAppAdmin) {
    return (
      <div className="p-6 text-center text-gray-600 bg-white rounded-lg m-4">
        You don't have permission to access this page. Requires superadmin access or App Administration permission.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center bg-white rounded-lg m-4">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading admin permissions...</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto bg-white rounded-lg m-4 shadow-sm">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" />
          App Administration
        </h1>
        <p className="text-gray-600 mt-1">
          Manage admin permissions and organization access for users
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'permissions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Admin </span>Permissions
          </button>
          <button
            onClick={() => setActiveTab('org_access')}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'org_access'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Organization </span>Access
            {orgAccessGrants.length > 0 && (
              <span className="ml-1 sm:ml-2 bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full text-xs">
                {orgAccessGrants.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Admin Permissions Tab */}
      {activeTab === 'permissions' && (
        <>
          {/* Permission Types Legend */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Available Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(PERMISSION_TYPES).map(([key, { label, icon: Icon, color, description }]) => (
                <div key={key} className="flex items-start gap-2 p-2 bg-white rounded border">
                  <div className={`p-1 rounded bg-${color}-100`}>
                    <Icon className={`w-4 h-4 text-${color}-600`} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{label}</div>
                    <div className="text-xs text-gray-500">{description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Users with Permissions Summary */}
          {usersWithAnyPermissions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Users with Admin Permissions ({usersWithAnyPermissions.length})
              </h3>
              <div className="bg-white rounded-lg border overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersWithAnyPermissions.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-indigo-700">
                                {u.first_name?.[0]}{u.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {u.first_name} {u.last_name}
                              </div>
                              <div className="text-xs text-gray-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {u.organization_name}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {u.admin_permissions?.map(perm => {
                              const permInfo = PERMISSION_TYPES[perm];
                              return (
                                <span
                                  key={perm}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${permInfo?.color || 'gray'}-100 text-${permInfo?.color || 'gray'}-700`}
                                >
                                  {permInfo?.label || perm}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openUserModal(u)}
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Search and Add Users */}
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Add Admin Permissions
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Users List */}
          {searchTerm && (
            <div className="bg-white rounded-lg border overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Permissions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.slice(0, 20).map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {u.first_name?.[0]}{u.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {u.first_name} {u.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {u.organization_name}
                      </td>
                      <td className="px-4 py-3">
                        {u.admin_permissions?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.admin_permissions.map(perm => {
                              const permInfo = PERMISSION_TYPES[perm];
                              return (
                                <span
                                  key={perm}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${permInfo?.color || 'gray'}-100 text-${permInfo?.color || 'gray'}-700`}
                                >
                                  {permInfo?.label || perm}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openUserModal(u)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length > 20 && (
                <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                  Showing 20 of {filteredUsers.length} users. Refine your search to see more.
                </div>
              )}
              {filteredUsers.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  No users found matching your search.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Organization Access Tab */}
      {activeTab === 'org_access' && (
        <>
          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 mb-1">About Organization Access</h3>
            <p className="text-sm text-blue-700">
              By default, users can only access data within their own organization.
              Grant organization access to allow users to view or edit data in other organizations.
            </p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs">
              <div className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-blue-600 flex-shrink-0" />
                <span className="text-blue-700"><strong>View Only:</strong> Can view trips, executives, etc.</span>
              </div>
              <div className="flex items-center gap-1">
                <Edit3 className="w-3 h-3 text-green-600 flex-shrink-0" />
                <span className="text-blue-700"><strong>View & Edit:</strong> Can view and modify data</span>
              </div>
            </div>
          </div>

          {/* Current Organization Access Grants */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                Organization Access Grants ({orgAccessGrants.length})
              </h3>
            </div>

            {orgAccessGrants.length > 0 ? (
              <div className="bg-white rounded-lg border overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Has Access To</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Access Level</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Granted</th>
                      <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orgAccessGrants.map(grant => (
                      <tr key={grant.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{grant.user_name}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-none">{grant.user_email}</div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
                            <span className="text-sm text-gray-900">{grant.organization_name}</span>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                            grant.access_level === 'view_edit'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {grant.access_level === 'view_edit' ? (
                              <><Edit3 className="w-3 h-3" /> <span className="hidden sm:inline">View & </span>Edit</>
                            ) : (
                              <><Eye className="w-3 h-3" /> View<span className="hidden sm:inline"> Only</span></>
                            )}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                          {new Date(grant.granted_at).toLocaleDateString()}
                          <div className="text-xs">by {grant.granted_by_email}</div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right">
                          <button
                            onClick={() => handleRevokeOrgAccess(grant.user_id, grant.organization_id)}
                            disabled={saving[`revoke-${grant.user_id}-${grant.organization_id}`]}
                            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          >
                            {saving[`revoke-${grant.user_id}-${grant.organization_id}`] ? (
                              <Loader2 className="w-4 h-4 animate-spin inline" />
                            ) : (
                              <><Trash2 className="w-4 h-4 inline sm:mr-1" /><span className="hidden sm:inline"> Revoke</span></>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                No organization access grants have been created yet.
              </div>
            )}
          </div>

          {/* Grant New Access */}
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Grant Organization Access
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users to grant organization access..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Users List for Org Access */}
          {searchTerm && (
            <div className="bg-white rounded-lg border overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Home Org</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap hidden sm:table-cell">Current Access</th>
                    <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.slice(0, 20).map(u => {
                    const userGrants = orgAccessGrants.filter(g => g.user_id === u.id);
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-medium text-gray-600">
                                {u.first_name?.[0]}{u.last_name?.[0]}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {u.first_name} {u.last_name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-sm text-gray-600">
                          {u.organization_name}
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                          {userGrants.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {userGrants.map(g => (
                                <span
                                  key={g.id}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                    g.access_level === 'view_edit'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {g.organization_name}
                                  {g.access_level === 'view_edit' ? (
                                    <Edit3 className="w-3 h-3" />
                                  ) : (
                                    <Eye className="w-3 h-3" />
                                  )}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Own organization only</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right">
                          <button
                            onClick={() => openOrgAccessModal(u)}
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium whitespace-nowrap"
                          >
                            <span className="hidden sm:inline">Grant Access</span>
                            <span className="sm:hidden">Grant</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredUsers.length > 20 && (
                <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                  Showing 20 of {filteredUsers.length} users. Refine your search to see more.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Audit Log Toggle */}
      <div className="mt-6">
        <button
          onClick={() => {
            setShowAuditLog(!showAuditLog);
            if (!showAuditLog) loadAuditLog();
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <History className="w-4 h-4" />
          <span>Audit Log</span>
          {showAuditLog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showAuditLog && (
          <div className="mt-3 bg-white rounded-lg border overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Permission</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm">
                {auditLog.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-4 py-2 text-gray-500 whitespace-nowrap text-xs sm:text-sm">
                      {new Date(log.performed_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 sm:px-4 py-2 text-gray-900 truncate max-w-[100px] sm:max-w-none">{log.user_email}</td>
                    <td className="px-3 sm:px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.action === 'granted' || log.action === 'updated'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2 text-gray-600 hidden sm:table-cell">
                      {log.permission_type.startsWith('org_access:')
                        ? `Org Access: ${log.permission_type.split(':')[1]?.substring(0, 8)}...`
                        : PERMISSION_TYPES[log.permission_type]?.label || log.permission_type
                      }
                    </td>
                    <td className="px-3 sm:px-4 py-2 text-gray-500 hidden md:table-cell truncate max-w-[150px]">{log.performed_by_email}</td>
                  </tr>
                ))}
                {auditLog.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No audit log entries yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Permissions Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Manage Admin Permissions
                </h2>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900">
                  {selectedUser.first_name} {selectedUser.last_name}
                </div>
                <div className="text-sm text-gray-500">{selectedUser.email}</div>
                <div className="text-sm text-gray-500">{selectedUser.organization_name}</div>
              </div>

              <div className="space-y-3">
                {Object.entries(PERMISSION_TYPES).map(([key, { label, icon: Icon, color, description }]) => {
                  const hasPermission = selectedUser.admin_permissions?.includes(key);

                  return (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border ${hasPermission ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-${color}-100`}>
                            <Icon className={`w-5 h-5 text-${color}-600`} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-xs text-gray-500">{description}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const newPermissions = hasPermission
                              ? selectedUser.admin_permissions.filter(p => p !== key)
                              : [...(selectedUser.admin_permissions || []), key];
                            setSelectedUser({ ...selectedUser, admin_permissions: newPermissions });
                          }}
                          className={`w-10 h-6 rounded-full relative transition-colors ${
                            hasPermission ? 'bg-indigo-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              hasPermission ? 'left-5' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSetAllPermissions(selectedUser.id, selectedUser.admin_permissions)}
                  disabled={saving[selectedUser.id]}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving[selectedUser.id] ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Access Modal */}
      {showOrgAccessModal && selectedUserForOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Grant Organization Access
                </h2>
                <button
                  onClick={() => {
                    setShowOrgAccessModal(false);
                    setSelectedUserForOrg(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900">
                  {selectedUserForOrg.first_name} {selectedUserForOrg.last_name}
                </div>
                <div className="text-sm text-gray-500">{selectedUserForOrg.email}</div>
                <div className="text-sm text-gray-500">Home: {selectedUserForOrg.organization_name}</div>
              </div>

              <div className="space-y-4">
                <div>
                  <OrganizationPicker
                    organizations={getAvailableOrgsForUser(selectedUserForOrg.id)}
                    value={selectedOrg}
                    onChange={(orgId) => setSelectedOrg(orgId)}
                    placeholder="Select organization..."
                    label="Organization to grant access to"
                    showDomain
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Level
                  </label>
                  <div className="space-y-2">
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      selectedAccessLevel === 'view_only' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="accessLevel"
                        value="view_only"
                        checked={selectedAccessLevel === 'view_only'}
                        onChange={(e) => setSelectedAccessLevel(e.target.value)}
                        className="text-blue-600"
                      />
                      <div>
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <Eye className="w-4 h-4 text-blue-600" />
                          View Only
                        </div>
                        <div className="text-xs text-gray-500">Can view trips, executives, and other data</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      selectedAccessLevel === 'view_edit' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="accessLevel"
                        value="view_edit"
                        checked={selectedAccessLevel === 'view_edit'}
                        onChange={(e) => setSelectedAccessLevel(e.target.value)}
                        className="text-green-600"
                      />
                      <div>
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <Edit3 className="w-4 h-4 text-green-600" />
                          View & Edit
                        </div>
                        <div className="text-xs text-gray-500">Can view and modify trips, executives, and other data</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowOrgAccessModal(false);
                    setSelectedUserForOrg(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGrantOrgAccess}
                  disabled={!selectedOrg || saving.orgAccess}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving.orgAccess ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Granting...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Grant Access
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPermissionsManagement;
